import type { MarketOutlookDTO, MarketOutlookTimeSeriesDTO, ValueWithData } from "@eu.mdotm.api/sphere";
import { GraphLegendLabel, GraphLegendTextKey } from "$root/components/GraphLegend";
import DonoutCircle from "$root/components/icons/DonoutCircle";
import { handlePreviousMarketData } from "$root/functional-areas/market-view/marketUtils";
import useWidgetsData from "$root/hooks/useWidgetsData";
import { PaletteColors } from "$root/styles/themePalette";
import { qualifier } from "$root/utils";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { useTranslation } from "react-i18next";
import OutlookBlockTable, { type TableParams } from "../OutlookBlock/OutlookBlockTable";
import { customOutlookBlockRepository } from "$root/functional-areas/customOutlookBlock/repository";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { TFunction } from "i18next";

type CustomSectorModel = {
	label: string;
	type: string;
	prevValue?: ValueWithData;
	currentValue?: ValueWithData;
};

const CustomOutlookBlock = (): React.JSX.Element => {
	const { t } = useTranslation();

	const { currentWidgetsData } = useWidgetsData();
	const dateRange = new Date(Date.parse(currentWidgetsData.creation_time.toString()));
	dateRange.setFullYear(dateRange.getFullYear() - 1);
	dateRange.setHours(0, 0, 0, 0);

	const equityGlobalQuery = useMappedQueries(
		(result) => outlookBlockTransformer(t, result),
		[t],
		[
			customOutlookBlockRepository.getEquitiesTimeSeriesContinuous.useQuery({
				geography: "GLOBAL",
				fromTime: dateRange.toISOString().split("T")[0],
			}),
			customOutlookBlockRepository.dashboardControllerGetEquities.useQuery("GLOBAL"),
		],
	);

	const equityEuQuery = useMappedQueries(
		(result) => outlookBlockTransformer(t, result),
		[t],
		[
			customOutlookBlockRepository.getEquitiesTimeSeriesContinuous.useQuery({
				geography: "EU",
				fromTime: dateRange.toISOString().split("T")[0],
			}),
			customOutlookBlockRepository.dashboardControllerGetEquities.useQuery("EU"),
		],
	);

	const equityUsaQuery = useMappedQueries(
		(result) => outlookBlockTransformer(t, result),
		[t],
		[
			customOutlookBlockRepository.getEquitiesTimeSeriesContinuous.useQuery({
				geography: "US",
				fromTime: dateRange.toISOString().split("T")[0],
			}),
			customOutlookBlockRepository.dashboardControllerGetEquities.useQuery("US"),
		],
	);

	const fixedIncomeQuery = useMappedQueries(
		(result) => outlookBlockTransformer(t, result),
		[t],
		[
			customOutlookBlockRepository.getFiiContinuousTimeSeries.useQuery({
				fromTime: dateRange.toISOString().split("T")[0],
			}),
			customOutlookBlockRepository.dashboardControllerGetFii.useQuery(),
		],
	);

	const commoditiesQuery = useMappedQueries(
		(result) => outlookBlockTransformer(t, result),
		[t],
		[
			customOutlookBlockRepository.getSectorContinuousTimeSeries.useQuery({
				fromTime: dateRange.toISOString().split("T")[0],
			}),
			customOutlookBlockRepository.dashboardControllerGetSector.useQuery(),
		],
	);

	useWidgetOptions(
		() => ({
			title: t("OUTLOOK.OUTLOOK_TITLE"),
		}),
		[t],
	);

	return (
		// Container-, not viewport-relative: a pinned Sphere AI panel narrows this widget without narrowing
		// the window (see WidgetsGrid.css). The thresholds are the old `lg:`/`xl:` viewport ones minus the
		// chrome the widget never had — the 61px nav rail and the page's 15px gutters.
		<div className="@container relative mt-6">
			<div className="grid grid-cols-1 @min-[933px]:grid-cols-2 @min-[1189px]:grid-cols-3 gap-6 gap-y-10 mb-8">
				<div className="h-122">
					<ReactQueryWrapperBase query={equityGlobalQuery}>
						{(equity) => (
							<OutlookBlockTable
								title="EQUITY"
								data={equity?.assetClass ?? []}
								assetClassTimeSeries={equity?.assetClassTimeSeries ?? {}}
								qualifier={qualifier.customReport.customOutlookTable("Equity")}
							/>
						)}
					</ReactQueryWrapperBase>
				</div>
				<div className="h-122">
					<ReactQueryWrapperBase query={equityEuQuery}>
						{(equityEu) => (
							<OutlookBlockTable
								title="EQUITY_EU"
								data={equityEu?.assetClass ?? []}
								assetClassTimeSeries={equityEu?.assetClassTimeSeries ?? {}}
								qualifier={qualifier.customReport.customOutlookTable("EquityEu")}
							/>
						)}
					</ReactQueryWrapperBase>
				</div>
				<div className="h-122">
					<ReactQueryWrapperBase query={equityUsaQuery}>
						{(equityUsa) => (
							<OutlookBlockTable
								title="EQUITY_USA"
								data={equityUsa?.assetClass ?? []}
								assetClassTimeSeries={equityUsa?.assetClassTimeSeries ?? {}}
								qualifier={qualifier.customReport.customOutlookTable("EquityUsa")}
							/>
						)}
					</ReactQueryWrapperBase>
				</div>
				<div className="h-122">
					<ReactQueryWrapperBase query={fixedIncomeQuery}>
						{(fixedIncome) => (
							<OutlookBlockTable
								title="FIXED_INCOME"
								data={fixedIncome?.assetClass ?? []}
								assetClassTimeSeries={fixedIncome?.assetClassTimeSeries ?? {}}
								qualifier={qualifier.customReport.customOutlookTable("FixedIncome")}
							/>
						)}
					</ReactQueryWrapperBase>
				</div>
				<div className="h-122">
					<ReactQueryWrapperBase query={commoditiesQuery}>
						{(commodities) => (
							<OutlookBlockTable
								title="COMMODITIES"
								data={commodities?.assetClass ?? []}
								assetClassTimeSeries={commodities?.assetClassTimeSeries ?? {}}
								qualifier={qualifier.customReport.customOutlookTable("Commodities")}
							/>
						)}
					</ReactQueryWrapperBase>
				</div>
			</div>
			<div className="flex gap-4">
				<div className="flex items-center">
					<Icon className="mr-0!" icon="previous-position" color={PaletteColors.AZURE} size={16} />
					<Icon className="mr-0!" icon="previous-position" color={PaletteColors.NEON_RED} size={16} />
					<Icon className="mr-0!" icon="previous-position" color={PaletteColors.GREEN_BLUE} size={16} />
					<GraphLegendLabel>{t("CURRENT_POSITIONING")}</GraphLegendLabel>
				</div>
				<div className="flex items-center gap-1">
					<DonoutCircle />
					<GraphLegendLabel>{t("PREVIOUS_POSITIONING")}</GraphLegendLabel>
				</div>
				<div className="flex items-center">
					<GraphLegendTextKey>{t("GRAPH_LEGEND.SU_KEY")}</GraphLegendTextKey>
					<GraphLegendLabel>{t("GRAPH_LEGEND.SU_LABEL")}</GraphLegendLabel>
				</div>
				<div className="flex items-center">
					<GraphLegendTextKey>{t("GRAPH_LEGEND.U_KEY")}</GraphLegendTextKey>
					<GraphLegendLabel>{t("GRAPH_LEGEND.U_LABEL")}</GraphLegendLabel>
				</div>
				<div className="flex items-center">
					<GraphLegendTextKey>{t("GRAPH_LEGEND.N_KEY")}</GraphLegendTextKey>
					<GraphLegendLabel>{t("GRAPH_LEGEND.N_LABEL")}</GraphLegendLabel>
				</div>
				<div className="flex items-center">
					<GraphLegendTextKey>{t("GRAPH_LEGEND.O_KEY")}</GraphLegendTextKey>
					<GraphLegendLabel>{t("GRAPH_LEGEND.O_LABEL")}</GraphLegendLabel>
				</div>
				<div className="flex items-center">
					<GraphLegendTextKey>{t("GRAPH_LEGEND.SO_KEY")}</GraphLegendTextKey>
					<GraphLegendLabel>{t("GRAPH_LEGEND.SO_LABEL")}</GraphLegendLabel>
				</div>
			</div>
		</div>
	);
};

function outlookBlockTransformer(
	t: TFunction,
	[assetClassTimeSeries, microAssetClass]: [MarketOutlookTimeSeriesDTO[], MarketOutlookDTO[]],
): {
	assetClass: TableParams[];
	assetClassTimeSeries: { [key: string]: [number, number, boolean][] } | undefined;
} {
	const assetClassTimeSeriesMapped = assetClassTimeSeries.reduce<
		{ [key: string]: [number, number, boolean][] } | undefined
	>((subAcc, assetClass) => {
		const { type, values, inverted } = assetClass;

		if (!type) {
			return undefined;
		}

		return {
			...subAcc,
			[type]: values?.filter(([_x, y]) => y ?? false).map(([dateSeconds, y]) => [dateSeconds * 1000, y, inverted]) as [
				number,
				number,
				boolean,
			][],
		};
	}, {});

	const assetClassMapped = microAssetClass
		.map(
			({ label, type, prevValue, currentValue }): CustomSectorModel => ({
				label: label ?? "-",
				type: type ?? "-",
				prevValue,
				currentValue,
			}),
		)
		.reduce(
			(acc, { currentValue, prevValue, type }, i) => {
				if (currentValue?.value === undefined) {
					return acc;
				}
				const { previous, tooltip: graphTooltip } = handlePreviousMarketData(
					"dashboard",
					(position) => t("CUSTOM_REPORT.OUTLOOK", { returnObjects: true })[position],
					{ value: currentValue.value, data: currentValue.data },
					{ value: prevValue?.value, data: prevValue?.data },
				);

				return [
					...acc,
					{
						assetClass: type,
						current: currentValue.value,
						prev: previous,
						tooltip: graphTooltip,
						index: i,
					},
				];
			},
			[] as Array<TableParams>,
		)
		.sort(builtInSortFnFor("assetClass"));

	return { assetClass: assetClassMapped, assetClassTimeSeries: assetClassTimeSeriesMapped };
}

export default CustomOutlookBlock;
